/**
 * superpatronen-search.js
 *
 * Normalisiert Such-Queries für SUPERPATRONEN.
 * Beispiel: User tippt "ts8550" → an Shopify geht "ts 8550" → findet Canon Pixma TS 8550.
 *
 * Greift an drei Stellen:
 *   1. Predictive Search (Header-Dropdown beim Tippen) via fetch-Intercept
 *   2. Form-Submit (Enter im Suchfeld) via submit-Listener
 *   3. Direct-Links (/search?q=ts8550 aus extern) via Page-Load-Redirect
 *
 * Die Normalisierung ist UNSICHTBAR für den User: Das Eingabefeld wird nicht
 * live überschrieben. Nur der an Shopify gesendete Query wird transformiert.
 *
 * Datenbasis: Marken und Serien wurden aus dem aktiven Sortiment extrahiert
 * (14.040 Druckermodelle aus Products.csv). Bei größeren Sortiments-
 * änderungen beide Listen neu generieren.
 *
 * ──────────────────────────────────────────────────────────────────────
 * MATCH-MODI in insertSpacesAround:
 *
 *   'aggressive' (für Marken):
 *     Splittet vor und nach dem Token bei jedem Nicht-Whitespace-Zeichen.
 *     Notwendig, damit z.B. 'canonpixma' → 'canon pixma' funktioniert.
 *     Marken sind kurz, distinkt und kommen praktisch nicht als Substring
 *     normaler deutscher/englischer Wörter vor.
 *
 *   'boundary' (für Serien):
 *     Splittet nur dann, wenn das Token nachweislich eigenständig auftritt:
 *     vor dem Token muss bereits eine echte Wortgrenze stehen (Stringanfang,
 *     Whitespace oder Ziffer). Es gibt KEINEN Vor-Split — wenn vor dem
 *     Token ein Buchstabe steht, ist das Token Substring eines anderen Worts
 *     und wird ignoriert.
 *
 *     Konkrete Trennung:
 *       - 'expression' / 'expressi' / 'express' → vor 'xpress' steht 'e'
 *         (Buchstabe) → unangetastet, Shopify findet Epson Expression
 *       - 'samsungxpress' → Regel B (Marken aggressive) trennt zuerst zu
 *         'samsung xpress' → Boundary erfüllt, falls noch Modellcode folgt
 *         wird nachgesplittet
 *       - '412xpress' → Regel A trennt zuerst zu '412 xpress'
 *       - 'xpressm2026' → Regel A trennt 'm'→'2' zu 'xpressm 2026', dann
 *         Boundary-Nach-Split zu 'xpress m 2026'
 * ──────────────────────────────────────────────────────────────────────
 */

(function () {
  'use strict';

  // ═══════════════════════════════════════════════════════════════════
  // DATEN
  // ═══════════════════════════════════════════════════════════════════

  // Regel B — alle Marken im aktiven Sortiment
  const BRANDS = [
    'Brother', 'Canon', 'Dell', 'Develop', 'Epson', 'G&G', 'HP', 'IBM',
    'Kodak', 'Konica', 'Kyocera', 'Lexmark', 'OKI', 'Olivetti', 'Panasonic',
    'Philips', 'Ricoh', 'Samsung', 'Sharp', 'Tally', 'Toshiba', 'Utax', 'Xerox'
  ];

  // Regel C — Serien-Namen mit ≥20 Modellen, ohne generische Begriffe
  // (generische wie "Color", "Fax", "Pro", "Envy", "Smart" würden Fehlsplits produzieren)
  const SERIES = [
    'LaserJet', 'DeskJet', 'Pixma', 'i-SENSYS', 'OfficeJet', 'PhotoSmart',
    'imageRUNNER', 'WorkForce', 'EcoTank', 'Aficio', 'P-Touch', 'ECOSYS',
    'Stylus', 'PSC', 'DesignJet', 'WorkCentre', 'Phaser', 'imagePROGRAF',
    'Minolta', 'MFC-J', 'Xpress', 'Microline', 'SureColor', 'HL-L',
    'Optra', 'Ineo', 'VersaLink', 'TASKalfa', 'MFC-L', 'Aculaser',
    'iR-C', 'Infoprint', 'ProXpress', 'PageWide', 'Maxify', 'MX-M',
    'DCP-L', 'KX-MB', 'MultiXpress', 'iR-ADV', 'DCP-J', 'BJC',
    'TM-U', 'Selphy', 'Intellifax', 'SL-M', 'Multipass', 'KX-FLB',
    'Genicom', 'FS-C', 'MX-B', 'Lasershot', 'D-Color', 'ECR',
    'KX-FL', 'PLQ', 'P-C', 'AltaLink'
  ];

  // Längere Tokens zuerst matchen (wichtig z.B. für "imagePROGRAF" vor "image")
  const SORTED_BRANDS = [...BRANDS].sort((a, b) => b.length - a.length);
  const SORTED_SERIES = [...SERIES].sort((a, b) => b.length - a.length);

  // ═══════════════════════════════════════════════════════════════════
  // NORMALIZE — Kern der Logik
  // ═══════════════════════════════════════════════════════════════════

  function normalize(query) {
    if (!query || typeof query !== 'string') return query;
    let q = query.trim();
    if (!q) return query;

    // Patronen-Suffixe: Diese Kombinationen NICHT von der Zahl trennen,
    // weil sie in Produkttiteln als ein Token stehen (z.B. "16XL", "302XL").
    // Ergänzen, falls Tests weitere Fälle zeigen.
    const CARTRIDGE_SUFFIXES = ['XL', 'XXL', 'XXXL', 'HC'];

    // Regel A (Teil 1): Buchstabe→Zahl trennen
    //   'ts8550' → 'ts 8550'
    q = q.replace(/([a-zA-Z])(\d)/g, '$1 $2');

    // Regel A (Teil 2): Zahl→Buchstabe trennen, AUSSER bei Patronen-Suffix
    //   'm479dw' (nach Teil 1: 'm 479dw') → 'm 479 dw'
    //   '302xl' bleibt '302xl'  (XL ist Patronen-Suffix)
    q = q.replace(/(\d+)([a-zA-Z]+)/g, (match, digits, letters) => {
      if (CARTRIDGE_SUFFIXES.includes(letters.toUpperCase())) {
        return match;  // nicht splitten
      }
      return digits + ' ' + letters;
    });

    // Regel B: Marken-Namen aus zusammengeschriebenen Wörtern herauslösen
    //   'canonpixma' → 'canon pixma'
    // Mode 'aggressive': splittet vor UND nach dem Token bei jedem
    // Nicht-Whitespace-Nachbarn. Marken sind eindeutig genug.
    q = insertSpacesAround(q, SORTED_BRANDS, 'aggressive');

    // Regel C: Serien-Namen aus zusammengeschriebenen Wörtern herauslösen
    //   'hp laserjetm479' → 'hp laserjet m 479'
    // Mode 'boundary': splittet nach dem Token NUR wenn davor bereits eine
    // Wortgrenze steht. Verhindert, dass 'xpress' in 'express' / 'expression'
    // / 'expressi' fälschlich erkannt wird. Bei 'samsungxpressm2026' hat
    // Regel B vorher 'samsung' abgetrennt, bei '412xpress' hat Regel A die
    // Ziffer abgetrennt — in beiden Fällen ist die Boundary dann erfüllt.
    q = insertSpacesAround(q, SORTED_SERIES, 'boundary');

    // Mehrfache Leerzeichen kollabieren
    q = q.replace(/\s+/g, ' ').trim();

    return q;
  }

  /**
   * Fügt Leerzeichen vor und nach jedem Match in der Token-Liste ein.
   * Case-insensitive.
   *
   * @param {string} query - Der zu transformierende String.
   * @param {string[]} tokens - Liste der Tokens (Marken oder Serien).
   * @param {'aggressive'|'boundary'} mode - Match-Strategie.
   *   'aggressive' = splittet vor und nach Token bei jedem Nicht-WS-Nachbarn.
   *   'boundary'   = splittet nur nach Token, und nur wenn vor Token bereits
   *                  Stringanfang, Whitespace oder Ziffer steht.
   */
  function insertSpacesAround(query, tokens, mode) {
    let q = query;
    for (const token of tokens) {
      // Regex-Sonderzeichen escapen ('&' in G&G, '-' in P-Touch, usw.)
      const escaped = token.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

      if (mode === 'aggressive') {
        // Vor dem Token: jedes Nicht-Whitespace-Zeichen triggert Split.
        q = q.replace(new RegExp(`(\\S)(${escaped})`, 'gi'), '$1 $2');
        // Nach dem Token: jedes Nicht-Whitespace-Zeichen triggert Split.
        q = q.replace(new RegExp(`(${escaped})(\\S)`, 'gi'), '$1 $2');
      } else if (mode === 'boundary') {
        // KEIN Vor-Split. Steht vor dem Token ein Buchstabe, ist das Token
        // Substring eines anderen Worts ('xpress' in 'express') — nicht
        // anfassen. Steht vor dem Token bereits eine Boundary, ist Vor-Split
        // ohnehin unnötig.
        //
        // Nach-Split: nur trennen, wenn (a) vor dem Token bereits eine
        // Wortgrenze steht (Stringanfang, Whitespace oder Ziffer) UND
        // (b) direkt nach dem Token ein Nicht-Whitespace-Zeichen folgt.
        q = q.replace(
          new RegExp(`(^|\\s|\\d)(${escaped})(\\S)`, 'gi'),
          '$1$2 $3'
        );
      }
    }
    return q;
  }

  // ═══════════════════════════════════════════════════════════════════
  // INTEGRATION 1: Predictive Search (Fetch-Intercept)
  // ═══════════════════════════════════════════════════════════════════
  // Shopifys Predictive Search feuert fetch() an /search/suggest.
  // Wir fangen den Call ab, normalisieren den q-Parameter, leiten ihn weiter.
  // Der User merkt nichts — das Eingabefeld bleibt unverändert.

  const originalFetch = window.fetch;
  window.fetch = function (input, options) {
    try {
      const url = (typeof input === 'string') ? input : (input && input.url);
      if (url && typeof url === 'string' && url.includes('/search/suggest')) {
        const urlObj = new URL(url, window.location.origin);
        const q = urlObj.searchParams.get('q');
        if (q) {
          const normalized = normalize(q);
          if (normalized !== q) {
            urlObj.searchParams.set('q', normalized);
            const newUrl = urlObj.toString();
            if (typeof input === 'object' && input instanceof Request) {
              return originalFetch.call(this, new Request(newUrl, input), options);
            }
            return originalFetch.call(this, newUrl, options);
          }
        }
      }
    } catch (e) {
      console.warn('[SUPERPATRONEN Search] fetch-intercept failed, passing through:', e);
    }
    return originalFetch.call(this, input, options);
  };

  // ═══════════════════════════════════════════════════════════════════
  // INTEGRATION 2: Form-Submit (Enter im Suchfeld)
  // ═══════════════════════════════════════════════════════════════════
  // Beim Enter-Drücken wird das Input kurz vor dem Submit auf den
  // normalisierten Wert gesetzt. Danach navigiert der Browser zur
  // /search?q=…-URL. Auf der Result-Seite zeigt Shopify den URL-Param
  // ohnehin im Feld an — das ist dann die normalisierte Version,
  // was okay ist (User sieht, was gesucht wurde).

  function attachFormHandlers(root) {
    const forms = (root || document).querySelectorAll('form[action*="/search"]');
    forms.forEach(form => {
      if (form.__superpatronenAttached) return;
      form.__superpatronenAttached = true;

      form.addEventListener('submit', function () {
        const input = form.querySelector('input[name="q"]');
        if (!input) return;
        const original = input.value.trim();
        if (!original) return;
        const normalized = normalize(original);
        if (normalized !== original) {
          input.value = normalized;
        }
      });
    });
  }

  // ═══════════════════════════════════════════════════════════════════
  // INTEGRATION 3: Direct-Link-Redirect beim Page-Load
  // ═══════════════════════════════════════════════════════════════════
  // Wenn jemand /search?q=ts8550 per externem Link aufruft, greifen
  // weder Fetch noch Form-Submit. Wir prüfen beim Page-Load den
  // q-Parameter und leiten einmalig auf die normalisierte Version um.
  // Der Flag-Parameter '_sp=1' verhindert Redirect-Loops.

  function handleDirectLink() {
    if (!window.location.pathname.includes('/search')) return;
    const params = new URLSearchParams(window.location.search);
    const q = params.get('q');
    if (!q) return;
    if (params.get('_sp') === '1') return; // schon normalisiert
    const normalized = normalize(q);
    if (normalized !== q) {
      params.set('q', normalized);
      params.set('_sp', '1');
      const newUrl = window.location.pathname + '?' + params.toString() + window.location.hash;
      window.location.replace(newUrl);
    }
  }

  // ═══════════════════════════════════════════════════════════════════
  // BOOTSTRAP
  // ═══════════════════════════════════════════════════════════════════

  function init() {
    handleDirectLink();
    attachFormHandlers();
    // Forms, die später nachgeladen werden (z.B. Modals), auch abdecken
    const observer = new MutationObserver(mutations => {
      for (const m of mutations) {
        for (const node of m.addedNodes) {
          if (node.nodeType === 1) attachFormHandlers(node);
        }
      }
    });
    observer.observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Für Debugging in der Browser-Konsole:
  //   SuperpatronenSearch.normalize('ts8550')             → 'ts 8550'
  //   SuperpatronenSearch.normalize('hpm479dw')           → 'hp m 479 dw'
  //   SuperpatronenSearch.normalize('canonpixmats8550')   → 'canon pixma ts 8550'
  //   SuperpatronenSearch.normalize('epson express')      → 'epson express'      (unverändert)
  //   SuperpatronenSearch.normalize('epson expressi')     → 'epson expressi'     (unverändert)
  //   SuperpatronenSearch.normalize('epson expression')   → 'epson expression'   (unverändert)
  //   SuperpatronenSearch.normalize('samsungxpressm2026') → 'samsung xpress m 2026'
  //   SuperpatronenSearch.normalize('412xpress')          → '412 xpress'
  //   SuperpatronenSearch.normalize('302xl')              → '302xl'              (Patronen-Suffix)
  window.SuperpatronenSearch = { normalize };
})();